import PropTypes from 'prop-types';
// @flow

import React, { Component } from 'react';
import _ from 'lodash';
import I18n from 'PMT/i18n/I18n';

// -- stores
import {
   PaymentStatStore,
   SessionStore,
   RestaurantStore
 } from 'stores';

// -- actions
import {
  RouteAction,
  PaymentAction
} from 'actions';

// -- constants
import RoutingEnum from 'constants/RoutingEnum';
import Events from 'constants/Events';

// -- entities
import PaymentStatistics from 'PMT/entities/PaymentStatistics';
import Timelapse from 'PMT/entities/Timelapse';
import Payin from 'PMT/entities/Payin';

// -- utils
import PaymentStatsGraphInfo from 'objects/PaymentStatsGraphInfo';
import DateUtils from 'PMT/utils/DateUtils.js';


// -- components
import AbstractComponent from 'components/AbstractComponent';
import Ariane from 'PMT/components/Ariane';
import TimelapsePicker from 'components/timelapse/TimelapsePicker';
import PaymentStatsGraph from './components/PaymentStatsGraph';
import PaymentStatsKeyNumbers from './components/PaymentStatsKeyNumbers';

export default class StatPage extends AbstractComponent {

  constructor() {
    super();

    this.arianeIcon = 'fa-line-chart';
    this.arianeDetails = [
      {
        name: I18n.tr('menu.payment'),
        link: ''
      },
      {
        name: I18n.tr('menu.payment.stat'),
        link: ''
      }
    ];
  }

  state = {
    paymentStatistics: PaymentStatStore.getPaymentStatistics(),
    timelapse: SessionStore.getTimelapse(),
    restaurant: RestaurantStore.getRestaurant(),
  };

  componentDidMount() {
    if (!RestaurantStore.getRestaurant().haveFeaturePayment()
      && !RestaurantStore.getRestaurant().haveFeatureOrder()
    ) {
      RouteAction.redirectToFeatureNonActivated();
      return;
    }

    PaymentStatStore.addListener(this.onChange);

    this.loadStatistics(this.state.timelapse);
  }

  componentWillUnmount() {
    PaymentStatStore.removeListener(this.onChange);
  }

  loadStatistics(timelapse: Timelapse) {
    this.showLoading();
    PaymentAction.loadStatistics(timelapse);
  }

  // -- Event functions

  onChange = () => {
    const paymentStatistics: PaymentStatistics = PaymentStatStore.getPaymentStatistics();

    let timelapse = null;
    if (!_.isNull(paymentStatistics)) {
      timelapse = paymentStatistics.timelapse;
      SessionStore.saveTimelapse(timelapse);
    }

    this.setState({
      paymentStatistics,
      timelapse: SessionStore.getTimelapse(),
      loading: false,
      apiError: PaymentStatStore.getApiError(),
    });
  };

  onTimelapseChange = (timelapse: Timelapse) => {
    this.loadStatistics(timelapse);
  };

  // -- Renderers

  renderPayins(paymentStatistics: PaymentStatistics) {
    const payins = [];

    let minDate = this.state.timelapse.getFromTimeAsSeconds();
    let maxDate = this.state.timelapse.getToTimeAsSeconds();

    paymentStatistics.payins.forEach((payin: Payin) => {
      if (payin.date < minDate) {
        minDate = payin.date;
      }
      if (payin.date > maxDate) {
        maxDate = payin.date;
      }
    });

    const SECONDS_IN_DAY = 86400; // to ms
    const MAX_INTERVAL = SECONDS_IN_DAY * 365; // approximately one year)

    // -- verify that the interval is not too big
    if (maxDate < minDate || (maxDate - minDate > MAX_INTERVAL)) {
      // interval to big;
      return (
        <div>
          {I18n.tr('payment_stats.interval_too_big')}
        </div>
      );
    }

    function getDayTimestampFromTimestamp(timestamp: Number): Number { // <-- inner function
      const format = 'DD-MM-YY';
      // get the string according to the format
      const dateString = DateUtils.fromTimestamp(timestamp).format(format);
      // convert the string to a date, and get the unix timestamp (seconds).
      return DateUtils.stringDateWithFormatToUnix(dateString, format);
    }

    // initiate the table. We need to create a default value from minDate to maxDate index.
    // Otherwise the graph will not be correct.
    for (let dateTimestamp = minDate;
         dateTimestamp < maxDate;
         dateTimestamp += SECONDS_IN_DAY) {
      payins[getDayTimestampFromTimestamp(dateTimestamp)] = 0;
    }

    // populate payins array
    paymentStatistics.payins.forEach((payin: Payin) => {
      const key = getDayTimestampFromTimestamp(payin.date);
      const alreadySet = _.isUndefined(key) || _.isNaN(key) ? 0 : payins[key];
      payins[key] = alreadySet + (payin.amount + payin.tips);
    });

    // format payins array to array of {period, total}
    const payinsFormatted: Array<PaymentStatsGraphInfo> = [];

    // Note: DO NOT USE _.mapKeys, it will crash.
    for (const key: string in payins) {
      if (payins.hasOwnProperty(key)) {
        const value: number = payins[key];
        payinsFormatted.push(new PaymentStatsGraphInfo(key, value));
      }
    }

    return (
      <div className="stat_page__data">

        {this.state.timelapse.isSameDay() === false &&
            <PaymentStatsGraph data={payinsFormatted}
                               currencyCode={this.state.restaurant.currency}/>
        }
        <div className="top-buffer-50"></div>

        <PaymentStatsKeyNumbers paymentStatistics={paymentStatistics}
                                currencyCode={this.state.restaurant.currency}/>
      </div>
    );
  }

  renderNoData() {
    // no information to display
    return (
      <div className="stat_page__no_data">
        <div className="col-xs-6 col-xs-offset-3 text-center">
          <i className="icon-smiley-down" />
          <p className="top-buffer-30">
            {I18n.tr('payment_stats.no_payout')}
          </p>
        </div>
      </div>
    );
  }

  render(): Object {
    const paymentStatistics: PaymentStatistics = this.state.paymentStatistics;

    let contentView = (null);

    let timelapsePickerView = (
      <div className="row">
        <TimelapsePicker
          className="col-xs-12 col-md-6 float-right text-right"
          timelapse={this.state.timelapse}
          onTimelapseChange={this.onTimelapseChange}
          timelapseSelection={SessionStore.getTimelapseSelection()}
        />
      </div>
    );

    if (_.isNull(paymentStatistics)) {
      contentView = this.renderOnLoadingContent();
      timelapsePickerView = (null);
    } else if (!_.isEmpty(paymentStatistics.payins)) {
      contentView = this.renderPayins(paymentStatistics);
    } else {
      contentView = this.renderNoData();
    }

    return (
      <div className="stat_page">

        <Ariane icon={this.arianeIcon} details={this.arianeDetails} />

        {this.renderLoading()}

        {timelapsePickerView}

        {contentView}

      </div>
    );
  }

}



// WEBPACK FOOTER //
// ./scripts/pages/payment/stat/StatPage.js